import React from "react";
import { Group, Loader, Stack, Text } from "@mantine/core";
import { TaxRate } from "../../types/taxRates";
import { formatTaxRatePercent } from "../../utils/taxRateFormat";

interface ProductTaxSummaryProps {
  taxRates: TaxRate[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Read-only "Applicable Tax" block for the product details Pricing section:
 * one line per linked rate, plus a combined total when several apply.
 */
export const ProductTaxSummary: React.FC<ProductTaxSummaryProps> = ({
  taxRates,
  isLoading,
  error,
}) => {
  const renderBody = () => {
    if (isLoading) return <Loader size="xs" aria-label="Loading tax rates" />;
    if (error) {
      return (
        <Text size="sm" c="red">
          {error}
        </Text>
      );
    }
    if (taxRates.length === 0) {
      return (
        <Text size="sm" fw={500}>
          None. This product isn't taxed.
        </Text>
      );
    }
    const total = taxRates.reduce((sum, rate) => sum + rate.rate, 0);
    return (
      <Stack gap={2}>
        {taxRates.map((rate) => (
          <Group key={rate.id} justify="space-between" wrap="nowrap" gap="xs">
            <Text size="sm">{rate.name}</Text>
            <Text size="sm" fw={500}>
              {formatTaxRatePercent(rate.rate)}
            </Text>
          </Group>
        ))}
        {taxRates.length > 1 && (
          <Group justify="space-between" wrap="nowrap" gap="xs">
            <Text size="sm" fw={600}>
              Total tax
            </Text>
            <Text size="sm" fw={600}>
              {formatTaxRatePercent(total)}
            </Text>
          </Group>
        )}
      </Stack>
    );
  };

  return (
    <Stack gap={4}>
      <Text size="sm" c="dimmed">
        Applicable Tax
      </Text>
      {renderBody()}
    </Stack>
  );
};

export default ProductTaxSummary;
